import React from 'react';
import { render, screen } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';

import RemoveRestoreModal from './RemoveRestoreModal';

const ACCOUNT = { id: 1, name: 'Account A' };

const renderModal = (props = {}) =>
  render(
    <ConfigProvider>
      <RemoveRestoreModal
        action="remove"
        email="abe@example.com"
        onClose={jest.fn()}
        onConfirm={jest.fn()}
        account={ACCOUNT}
        username="m.abe"
        visible
        {...props}
      />
    </ConfigProvider>,
  );

describe('management/EditUser RemoveRestoreModal', () => {
  it('renders nothing when not visible', () => {
    renderModal({ visible: false });

    expect(screen.queryByText(/Account A/)).not.toBeInTheDocument();
  });

  describe('removing', () => {
    it('names the account and the user in the consequence sentence', () => {
      renderModal({ action: 'remove' });

      expect(screen.getByText(/prevent/)).toHaveTextContent(
        'Clicking REMOVE ACCESS will prevent m.abe (email abe@example.com) from accessing Account A.',
      );
    });

    it('reassures that other accounts stay untouched', () => {
      renderModal({ action: 'remove' });

      expect(
        screen.getByText(
          'They will still have access to other accounts they belong to.',
        ),
      ).toBeInTheDocument();
    });

    it('shows Removing... while submitting', () => {
      renderModal({ action: 'remove', isSubmitting: true });

      expect(screen.getByText('Removing...')).toBeInTheDocument();
    });

    it('confirms via the primary action', () => {
      const onConfirm = jest.fn();
      renderModal({ action: 'remove', onConfirm });

      userEvent.click(
        screen.getAllByRole('button', { name: 'Remove Access' })[0],
      );

      expect(onConfirm).toHaveBeenCalled();
    });
  });

  describe('restoring', () => {
    it('names the account and the user in the consequence sentence', () => {
      renderModal({ action: 'restore' });

      expect(screen.getByText(/again grant/)).toHaveTextContent(
        'Clicking RESTORE ACCESS will again grant m.abe (email abe@example.com) access to Account A.',
      );
    });

    it('does not show the "other accounts" reassurance', () => {
      renderModal({ action: 'restore' });

      expect(
        screen.queryByText(
          'They will still have access to other accounts they belong to.',
        ),
      ).not.toBeInTheDocument();
    });

    it('shows Restoring... while submitting', () => {
      renderModal({ action: 'restore', isSubmitting: true });

      expect(screen.getByText('Restoring...')).toBeInTheDocument();
    });
  });

  it('cancels via the secondary action', () => {
    const onClose = jest.fn();
    renderModal({ onClose });

    userEvent.click(screen.getByRole('button', { name: 'Cancel' }));

    expect(onClose).toHaveBeenCalled();
  });

  // Per the ticket: a failure shows inline in the still-open dialog.
  it('shows the error inline when present', () => {
    renderModal({
      error: 'Something went wrong removing this account. Please try again.',
    });

    expect(screen.getByRole('alert')).toHaveTextContent(
      'Something went wrong removing this account. Please try again.',
    );
  });

  it('shows no error region when there is none', () => {
    renderModal();

    expect(screen.queryByRole('alert')).not.toBeInTheDocument();
  });

  describe('the error title', () => {
    it('reads "Access not removed." for a remove failure', () => {
      renderModal({ action: 'remove', error: 'boom' });

      expect(screen.getByRole('alert')).toHaveTextContent(
        'Access not removed.',
      );
    });

    it('reads "Access not restored." for a restore failure', () => {
      renderModal({ action: 'restore', error: 'boom' });

      expect(screen.getByRole('alert')).toHaveTextContent(
        'Access not restored.',
      );
    });
  });

  describe('with a retryable error', () => {
    it('leaves the primary action enabled', () => {
      renderModal({
        error: 'Something went wrong removing this account. Please try again.',
      });

      expect(
        screen.getAllByRole('button', { name: 'Remove Access' })[0],
      ).not.toBeDisabled();
    });

    it('leaves Cancel enabled', () => {
      renderModal({ error: 'boom' });

      expect(screen.getByRole('button', { name: 'Cancel' })).not.toBeDisabled();
    });
  });

  describe('with a terminal error', () => {
    it('disables the primary action', () => {
      renderModal({ error: 'boom', isTerminal: true });

      expect(
        screen.getAllByRole('button', { name: 'Remove Access' })[0],
      ).toBeDisabled();
    });

    it('still leaves Cancel enabled', () => {
      renderModal({ error: 'boom', isTerminal: true });

      expect(screen.getByRole('button', { name: 'Cancel' })).not.toBeDisabled();
    });
  });

  describe('the header icon', () => {
    it('renders for a remove action', () => {
      renderModal({ action: 'remove' });

      const svgs = screen.getByRole('dialog').querySelectorAll('svg');
      expect(svgs.length).toBeGreaterThan(1);
    });

    it('renders for a restore action', () => {
      renderModal({ action: 'restore' });

      const svgs = screen.getByRole('dialog').querySelectorAll('svg');
      expect(svgs.length).toBeGreaterThan(1);
    });
  });

  describe('with no error', () => {
    it('leaves the primary action enabled', () => {
      renderModal();

      expect(
        screen.getAllByRole('button', { name: 'Remove Access' })[0],
      ).not.toBeDisabled();
    });

    it('leaves Cancel enabled', () => {
      renderModal();

      expect(screen.getByRole('button', { name: 'Cancel' })).not.toBeDisabled();
    });
  });
});
